import axios from "axios";
import uitoolkit from "@zoom/videosdk-ui-toolkit";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentToken } from "@/App/services/authSlice";
import "@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css";
import Loader from "@/Components/loader";
import { useEndSessionMutation } from "@/App/services/sessionSlice.js";
import './custom.css';
import SessionNote from "@/App/components/videoChatPage/sessionnote.jsx";
import useAudioRecording from "@/App/components/videoChatPage/AudioRecorder.jsx";
import logger from "@/App/utils/logger";
import { toast } from "sonner";
import moment from "moment";

function VideoScreen() {
    const sessionContainerRef = useRef(null);
    const settingsContainerRef = useRef(null);
    const notesPanelRef = useRef(null);
    const [currentTime, setCurrentTime] = useState('');
    const [sessionData, setSessionData] = useState(null);
    const [sessionJoined, setSessionJoined] = useState(false);
    const [endSession, { isLoading: endLoading }] = useEndSessionMutation();
    const navigate = useNavigate();
    const { id } = useParams();
    const token = useSelector(selectCurrentToken);
    const baseUrl = import.meta.env.VITE_BASE_API_URL;
    const [showNotes, setShowNotes] = useState(false);
    const [isButtonVisible, setButtonVisible] = useState(false);
    const [zoomControlsRendered, setZoomControlsRendered] = useState(false);
    const endWarningShownRef = useRef(false);
    // Recording
    const { isSaving, startRecording, stopRecording } = useAudioRecording(baseUrl, id);


    // Fetch session data by ID
    useEffect(() => {
        const fetchSession = async () => {
            try {
                const response = await axios.get(`${baseUrl}/session/?id=${id}`);
                setSessionData(response.data);
            } catch (error) {
                logger.error('Error fetching session:', error);
            }
        };
        if (id) fetchSession();
    }, [id, baseUrl]);

    // Update time every second (hour:minute format only)
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            const hours = now.getHours();
            const minutes = now.getMinutes().toString().padStart(2, '0');
            setCurrentTime(`${hours}:${minutes}`);
        };
        updateTime(); // Set initial time immediately
        const intervalId = setInterval(updateTime, 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Toast when ~5 minutes left until session end (only once, dismissible or auto-dismiss after 10s)
    // Prefer starts_at_calendar (same as appointments list); fallback to starts_at
    useEffect(() => {
        const d = sessionData?.data;
        const startAt = d?.starts_at_calendar ?? d?.starts_at;
        if (!startAt || d?.duration == null || !sessionJoined) return;
        if (endWarningShownRef.current) return;

        const durationMins = Number(d.duration) || 0;
        const sessionEnd = moment(startAt).add(durationMins, 'minutes');
        const fiveMinsBeforeEnd = moment(sessionEnd).subtract(5, 'minutes');

        const check = () => {
            const now = moment();
            if (now.isSameOrAfter(fiveMinsBeforeEnd) && now.isBefore(sessionEnd) && !endWarningShownRef.current) {
                endWarningShownRef.current = true;
                toast.warning('Session ending in about 5 minutes.', { duration: 10000 });
            }
        };

        check();
        const intervalId = setInterval(check, 30000); // check every 30s
        return () => clearInterval(intervalId);
    }, [sessionData?.data, sessionJoined]);

    // Function to handle ending the session
    const handleEndSession = async (sessionId, reason) => {
        try {
            await endSession({ id: sessionId, reason: { reason } }).unwrap();
            stopRecording()
            // save an audio file
            // if (!isSaving) navigate("/dashboard/home");
            navigate("/dashboard/home")

        } catch (error) {
            logger.error("Failed to end session:", error);
        }
    };

    const authEndpoint = `${baseUrl}/zoom-sdk-auth`;
    const role = 1; // 1 for host, 2 for participant

    // Store event handler references for cleanup
    const sessionJoinedHandlerRef = useRef(null);
    const sessionClosedHandlerRef = useRef(null);

    // Fetch Video SDK JWT and join session
    const getVideoSDKJWT = () => {
        if (!sessionData || sessionJoined || !sessionContainerRef.current) return;

        // Validate session data before proceeding
        if (!sessionData?.data?.reference || !sessionData?.data?.therapist?.name) {
            logger.error('Invalid session data:', sessionData);
            return;
        }

        const sessionName = sessionData.data.reference;
        const userName = sessionData.data.therapist.name;

        const headers = {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
        };
        fetch(authEndpoint, {
            method: "POST",
            headers,
            body: JSON.stringify({ sessionName, role }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.signature) {
                    logger.info('JWT received:', data.signature);
                    joinSession(data.signature, sessionName, userName);
                } else {
                    logger.error('No signature received:', data);
                }
            })
            .catch((error) => {
                logger.error('Error fetching JWT:', error);
            });
    };


    // Join the session using UI Toolkit
    const joinSession = (videoSDKJWT, sessionName, userName) => {
        if (sessionJoined || !videoSDKJWT || !sessionContainerRef.current) {
            logger.warn('Cannot join session:', { sessionJoined, hasJWT: !!videoSDKJWT, hasContainer: !!sessionContainerRef.current });
            return;
        }

        try {
            // Create config object with validated values
            const config = {
                videoSDKJWT,
                sessionName,
                userName,
                sessionPasscode: "",
                features: ["video", "audio", "share", "chat", "settings"],
                options: {
                    init: {},
                    audio: {},
                    video: {
                        originalRatio: true
                    },
                    share: {
                        bShareToolBarVisible: true,
                        sharePrivilege: 0,
                        bShowShareButton: true,
                        bSupportAudioShare: true,
                        bSupportVideoShare: true
                    },
                },
            };

            logger.info('Joining session with config:', { sessionName, userName });
            uitoolkit.joinSession(sessionContainerRef.current, config);
            setSessionJoined(true);

            // Store handler references for cleanup
            const onJoinedHandler = () => {
                logger.info('Session joined');

                // Change the view to gallery after the Dom is fully loaded.
                setTimeout(() => {
                    const changeViewButton = document.querySelector('[aria-label="Change view"]');
                    if (changeViewButton) {
                        changeViewButton.click();

                        const galleryViewButton = [...document.querySelectorAll("span")].find((span) =>
                            span.textContent?.includes("Gallery")
                        );

                        if (galleryViewButton) {
                            galleryViewButton.click();
                            logger.debug("View switched to Gallery.");
                        } else {
                            logger.warn("Gallery view option not found.");
                        }
                    } else {
                        logger.warn("Change view button not found.");
                    }
                    setButtonVisible(true)
                }, 1000);
            };

            const onClosedHandler = async () => {
                logger.info('Session closed');
                handleEndSession(id, 'Session ended by the host').then(r => {
                    logger.info("Session ended successfully:", r);
                });
                setSessionJoined(false);
            };

            sessionJoinedHandlerRef.current = onJoinedHandler;
            sessionClosedHandlerRef.current = onClosedHandler;

            uitoolkit.onSessionJoined(onJoinedHandler);
            uitoolkit.onSessionClosed(onClosedHandler);
        } catch (error) {
            logger.error('Error joining session:', error);
            setSessionJoined(false);
        }
    };

    // Trigger JWT fetch and join when sessionData is available
    useEffect(() => {
        if (sessionData) {
            getVideoSDKJWT();
        }
        return () => {
            // Clean up event handlers properly
            if (sessionJoinedHandlerRef.current) {
                try {
                    uitoolkit.offSessionJoined(sessionJoinedHandlerRef.current);
                } catch (error) {
                    logger.warn('Error removing session joined handler:', error);
                }
            }
            if (sessionClosedHandlerRef.current) {
                try {
                    uitoolkit.offSessionClosed(sessionClosedHandlerRef.current);
                } catch (error) {
                    logger.warn('Error removing session closed handler:', error);
                }
            }
        };
    }, [sessionData]);


    // Start recording after three seconds on the meetings
    useEffect(() => {
        const timer = setTimeout(() => {
            startRecording().then(r => logger.info("Recording started...", r));
        }, 15000);

        return () => clearTimeout(timer);
    }, []);


    // Hide Zoom UI Toolkit header
    useEffect(() => {
        if (!sessionJoined) return;

        const hideZoomHeader = () => {
            // Target the Zoom UI Toolkit header by ID or class
            const zoomHeader = document.getElementById('uikit-header') ||
                document.querySelector('.uikit-header') ||
                document.querySelector('header[id="uikit-header"]') ||
                document.querySelector('header.uikit-header');

            if (zoomHeader) {
                zoomHeader.style.display = 'none';
                zoomHeader.style.visibility = 'hidden';
                zoomHeader.style.height = '0';
                zoomHeader.style.overflow = 'hidden';
                zoomHeader.style.margin = '0';
                zoomHeader.style.padding = '0';
                zoomHeader.style.border = 'none';
            }

            // Hide the sub-session button in footer
            const subSessionButton = document.getElementById('uikit-footer-subsession-button') ||
                document.querySelector('#uikit-footer-subsession-button') ||
                document.querySelector('[id="uikit-footer-subsession-button"]');

            if (subSessionButton) {
                subSessionButton.style.display = 'none';
                subSessionButton.style.visibility = 'hidden';
                subSessionButton.style.width = '0';
                subSessionButton.style.height = '0';
                subSessionButton.style.padding = '0';
                subSessionButton.style.margin = '0';
                subSessionButton.style.opacity = '0';
                subSessionButton.style.pointerEvents = 'none';
            }

            // Hide the participants button in footer
            const participantsButton = document.getElementById('uikit-footer-participants-button') ||
                document.querySelector('#uikit-footer-participants-button') ||
                document.querySelector('[id="uikit-footer-participants-button"]');

            if (participantsButton) {
                participantsButton.style.display = 'none';
                participantsButton.style.visibility = 'hidden';
                participantsButton.style.width = '0';
                participantsButton.style.height = '0';
                participantsButton.style.padding = '0';
                participantsButton.style.margin = '0';
                participantsButton.style.opacity = '0';
                participantsButton.style.pointerEvents = 'none';
            }

            // Hide the whiteboard button in footer
            const whiteboardButton = document.getElementById('uikit-footer-whiteboard-button') ||
                document.querySelector('#uikit-footer-whiteboard-button') ||
                document.querySelector('[id="uikit-footer-whiteboard-button"]');

            if (whiteboardButton) {
                whiteboardButton.style.display = 'none';
                whiteboardButton.style.visibility = 'hidden';
                whiteboardButton.style.width = '0';
                whiteboardButton.style.height = '0';
                whiteboardButton.style.padding = '0';
                whiteboardButton.style.margin = '0';
                whiteboardButton.style.opacity = '0';
                whiteboardButton.style.pointerEvents = 'none';
            }

            // Hide the leave-meeting button in footer
            const leaveMeetingButton = document.getElementById('leave-meeting-button') ||
                document.querySelector('#leave-meeting-button') ||
                document.querySelector('[id="leave-meeting-button"]');

            if (leaveMeetingButton) {
                leaveMeetingButton.style.display = 'none';
                leaveMeetingButton.style.visibility = 'hidden';
                leaveMeetingButton.style.width = '0';
                leaveMeetingButton.style.height = '0';
                leaveMeetingButton.style.padding = '0';
                leaveMeetingButton.style.margin = '0';
                leaveMeetingButton.style.opacity = '0';
                leaveMeetingButton.style.pointerEvents = 'none';
            }

            // Center all buttons in the footer container
            const footerContainer = document.getElementById('zoom-ui-toolkit-controls') ||
                document.querySelector('#zoom-ui-toolkit-controls') ||
                document.querySelector('footer[id="zoom-ui-toolkit-controls"]') ||
                document.querySelector('.uikit-footer');

            if (footerContainer) {
                // Find the inner flex container with buttons
                const flexContainer = footerContainer.querySelector('.flex') ||
                    footerContainer.querySelector('[class*="flex"]') ||
                    footerContainer.querySelector('[class*="space-x"]');

                if (flexContainer) {
                    // Center all buttons together
                    flexContainer.style.display = 'flex';
                    flexContainer.style.justifyContent = 'center';
                    flexContainer.style.alignItems = 'center';
                    flexContainer.style.gap = '0.5rem';
                    flexContainer.style.flexWrap = 'nowrap';

                    // Remove any space-between or space-around that might spread buttons
                    if (flexContainer.classList.contains('space-x-4')) {
                        flexContainer.classList.remove('space-x-4');
                    }
                }

                // Also style the footer container itself to center content
                footerContainer.style.display = 'flex';
                footerContainer.style.justifyContent = 'center';
                footerContainer.style.alignItems = 'center';
            }
        };

        // Try to hide header immediately and set up observer
        const tryHide = () => {
            hideZoomHeader();
        };

        const interval = setInterval(tryHide, 500);
        tryHide(); // Try immediately

        // Also observe for DOM changes
        const observer = new MutationObserver(() => {
            hideZoomHeader();
        });

        if (sessionContainerRef.current) {
            observer.observe(sessionContainerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'id']
            });
        }

        // Also observe the document body for header that might be added outside session container
        const bodyObserver = new MutationObserver(() => {
            hideZoomHeader();
        });

        bodyObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'id']
        });

        return () => {
            clearInterval(interval);
            observer.disconnect();
            bodyObserver.disconnect();
        };
    }, [sessionJoined]);

    // Move Zoom controls to header
    useEffect(() => {
        if (!sessionJoined) return;

        const moveControlsToHeader = () => {
            const controlsContainer = document.querySelector('.controlskit, [class*="controlskit"], .videosdk-uitoolkit-controls');
            const headerControlsContainer = document.getElementById('zoom-controls-header');

            if (controlsContainer && headerControlsContainer) {
                // Check if already moved
                if (controlsContainer.parentElement === headerControlsContainer) {
                    addTooltipsToZoomControls();
                    setZoomControlsRendered(true);
                    return;
                }

                // Move the controls to header
                headerControlsContainer.appendChild(controlsContainer);

                // Style the controls to fit in header
                controlsContainer.style.position = 'relative';
                controlsContainer.style.bottom = 'auto';
                controlsContainer.style.left = 'auto';
                controlsContainer.style.transform = 'none';
                controlsContainer.style.display = 'flex';
                controlsContainer.style.alignItems = 'center';
                controlsContainer.style.gap = '8px';
                controlsContainer.style.margin = '0';

                logger.debug('Zoom controls moved to header');

                // Add tooltips to Zoom control buttons
                addTooltipsToZoomControls();
                setZoomControlsRendered(true);
            }
        };

        const addTooltipsToZoomControls = () => {
            const buttons = document.querySelectorAll('#zoom-controls-header button, #zoom-controls-header .mdc-button, #zoom-controls-header .mdc-fab, #zoom-controls-header [role="button"]');

            buttons.forEach((button) => {
                // Hide sub-session/breakout rooms button (check multiple possible labels)
                const ariaLabel = button.getAttribute('aria-label') || '';
                const buttonText = button.textContent || '';
                const title = button.getAttribute('title') || '';
                const allText = `${ariaLabel} ${buttonText} ${title}`.toLowerCase();

                const isSubSessionButton =
                    allText.includes('breakout') ||
                    allText.includes('sub session') ||
                    allText.includes('sub-session') ||
                    allText.includes('breakout room') ||
                    allText.includes('breakout rooms') ||
                    button.querySelector('[class*="breakout"]') ||
                    button.querySelector('[class*="sub-session"]') ||
                    button.querySelector('[class*="subsession"]') ||
                    button.querySelector('[data-testid*="breakout"]') ||
                    button.querySelector('[data-testid*="sub-session"]');

                if (isSubSessionButton) {
                    button.style.display = 'none';
                    button.style.visibility = 'hidden';
                    button.style.width = '0';
                    button.style.height = '0';
                    button.style.padding = '0';
                    button.style.margin = '0';
                    return;
                }

                // Hide participants button (check by ID or label)
                const isParticipantsButton =
                    button.id === 'uikit-footer-participants-button' ||
                    allText.includes('participant') ||
                    allText.includes('participants') ||
                    button.querySelector('[id*="participants"]') ||
                    button.querySelector('[class*="participant"]');

                if (isParticipantsButton) {
                    button.style.display = 'none';
                    button.style.visibility = 'hidden';
                    button.style.width = '0';
                    button.style.height = '0';
                    button.style.padding = '0';
                    button.style.margin = '0';
                    button.style.opacity = '0';
                    button.style.pointerEvents = 'none';
                    return;
                }

                // Hide whiteboard button (check by ID or label)
                const isWhiteboardButton =
                    button.id === 'uikit-footer-whiteboard-button' ||
                    allText.includes('whiteboard') ||
                    button.querySelector('[id*="whiteboard"]') ||
                    button.querySelector('[class*="whiteboard"]');

                if (isWhiteboardButton) {
                    button.style.display = 'none';
                    button.style.visibility = 'hidden';
                    button.style.width = '0';
                    button.style.height = '0';
                    button.style.padding = '0';
                    button.style.margin = '0';
                    button.style.opacity = '0';
                    button.style.pointerEvents = 'none';
                    return;
                }

                // Hide leave-meeting button
                if (button.id === 'leave-meeting-button') {
                    button.style.display = 'none';
                    button.style.visibility = 'hidden';
                    button.style.width = '0';
                    button.style.height = '0';
                    button.style.padding = '0';
                    button.style.margin = '0';
                    button.style.opacity = '0';
                    button.style.pointerEvents = 'none';
                    return;
                }

                // Add left margin to leave button
                if (button.id === 'leave-button' ||
                    allText.includes('leave') ||
                    allText.includes('end call') ||
                    allText.includes('end meeting')) {
                    button.style.marginLeft = '1rem';
                }

                // Add reduced left margin to chat button
                if (button.id === 'uikit-footer-chat-button') {
                    button.style.marginLeft = '0.4rem';
                }

                // Add reduced (negative) left margin to more button
                if (button.id === 'footer-more-button') {
                    button.style.marginLeft = '-0.5rem';
                }

                // Skip if tooltip already exists
                if (button.querySelector('.zoom-tooltip')) return;

                if (!ariaLabel) return;

                // Create tooltip element
                const tooltip = document.createElement('span');
                tooltip.className = 'zoom-tooltip';
                tooltip.textContent = ariaLabel;

                // Ensure button allows overflow
                button.style.overflow = 'visible';

                button.appendChild(tooltip);
            });
        };

        // Try to move controls immediately and also set up observer
        const tryMove = () => {
            moveControlsToHeader();
        };

        // Try multiple times as controls might render with delay
        const interval = setInterval(tryMove, 500);
        tryMove(); // Try immediately

        // Also observe for DOM changes
        const observer = new MutationObserver(() => {
            moveControlsToHeader();
        });

        if (sessionContainerRef.current) {
            observer.observe(sessionContainerRef.current, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            clearInterval(interval);
            observer.disconnect();
        };
    }, [sessionJoined]);

    // Center all buttons in Zoom footer container
    useEffect(() => {
        if (!sessionJoined) return;

        const centerFooterButtons = () => {
            // Find the footer container
            const footerContainer = document.getElementById('zoom-ui-toolkit-controls') ||
                document.querySelector('#zoom-ui-toolkit-controls') ||
                document.querySelector('footer[id="zoom-ui-toolkit-controls"]') ||
                document.querySelector('.uikit-footer');

            if (footerContainer) {
                // Style the footer container to center content
                footerContainer.style.display = 'flex';
                footerContainer.style.justifyContent = 'center';
                footerContainer.style.alignItems = 'center';

                // Find all flex containers inside the footer
                const flexContainers = footerContainer.querySelectorAll('.flex, [class*="flex"]');

                flexContainers.forEach((flexContainer) => {
                    // Check if it has space-x-4 or similar spacing classes
                    const hasSpaceX = Array.from(flexContainer.classList).some(cls =>
                        cls.startsWith('space-x-')
                    );

                    if (hasSpaceX) {
                        // Remove space-x classes effect by overriding with gap
                        flexContainer.style.gap = '0.5rem';
                    }

                    // Center all buttons
                    flexContainer.style.display = 'flex';
                    flexContainer.style.justifyContent = 'center';
                    flexContainer.style.alignItems = 'center';
                    flexContainer.style.flexWrap = 'nowrap';

                    // Add left margin to leave button
                    const leaveButton = flexContainer.querySelector('#leave-button') ||
                        Array.from(flexContainer.querySelectorAll('button')).find(btn => {
                            const ariaLabel = btn.getAttribute('aria-label') || '';
                            const text = btn.textContent || '';
                            return btn.id === 'leave-button' ||
                                ariaLabel.toLowerCase().includes('leave') ||
                                ariaLabel.toLowerCase().includes('end') ||
                                text.toLowerCase().includes('leave');
                        });

                    if (leaveButton) {
                        leaveButton.style.marginLeft = '1rem';
                    }

                    // Add reduced left margin to chat button
                    const chatButton = flexContainer.querySelector('#uikit-footer-chat-button');
                    if (chatButton) {
                        chatButton.style.marginLeft = '0.4rem';
                    }

                    // Add reduced (negative) left margin to more button
                    const moreButton = flexContainer.querySelector('#footer-more-button');
                    if (moreButton) {
                        moreButton.style.marginLeft = '-0.5rem';
                    }
                });
            }
        };

        // Try to center buttons immediately and set up observer
        const tryCenter = () => {
            centerFooterButtons();
        };

        const interval = setInterval(tryCenter, 500);
        tryCenter(); // Try immediately

        // Also observe for DOM changes
        const observer = new MutationObserver(() => {
            centerFooterButtons();
        });

        if (sessionContainerRef.current) {
            observer.observe(sessionContainerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'id']
            });
        }

        // Also observe the document body
        const bodyObserver = new MutationObserver(() => {
            centerFooterButtons();
        });

        bodyObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'id']
        });

        return () => {
            clearInterval(interval);
            observer.disconnect();
            bodyObserver.disconnect();
        };
    }, [sessionJoined]);

    // Add participant name labels
    useEffect(() => {
        if (!sessionJoined) return;

        const addParticipantNames = () => {
            const galleryItems = document.querySelectorAll('.videosdk-uitoolkit-gallery-item');

            galleryItems.forEach((item, index) => {
                let participantName = '';

                // Method 1: Look for name in Zoom SDK participant name elements
                // Zoom SDK often uses specific class names for participant names
                const nameSelectors = [
                    '[class*="participant-name"]',
                    '[class*="user-name"]',
                    '[class*="display-name"]',
                    '[class*="name-label"]',
                    '.name',
                    '[aria-label]',
                    'span[title]',
                    'div[title]'
                ];

                for (const selector of nameSelectors) {
                    const nameElement = item.querySelector(selector);
                    if (nameElement) {
                        const text = nameElement.textContent?.trim() ||
                            nameElement.getAttribute('aria-label')?.trim() ||
                            nameElement.getAttribute('title')?.trim() ||
                            nameElement.getAttribute('data-name')?.trim() || '';
                        if (text && text.length > 0 && !text.includes('has joined') && !text.includes('joined')) {
                            participantName = text;
                            break;
                        }
                    }
                }

                // Method 2: Look for name in notification messages (e.g., "John has joined")
                if (!participantName) {
                    const notifications = document.querySelectorAll('[class*="notification"], [class*="toast"], [class*="message"], [class*="snackbar"]');
                    for (const notif of notifications) {
                        const text = notif.textContent || '';
                        if (text.includes('has joined') || text.includes('joined')) {
                            const match = text.match(/(.+?)\s+(has joined|joined)/i);
                            if (match && match[1]) {
                                participantName = match[1].trim();
                                break;
                            }
                        }
                    }
                }

                // Method 3: Use session data for therapist (first participant)
                if (!participantName && index === 0) {
                    participantName = sessionData?.data?.therapist?.name || 'Therapist';
                }

                // Method 4: Use session data for patient (second participant)
                if (!participantName && index === 1) {
                    participantName = sessionData?.data?.user?.name ||
                        sessionData?.data?.client?.name ||
                        sessionData?.data?.patient?.name ||
                        'Patient';
                }

                // Method 5: Look for name in video element's title or alt
                if (!participantName) {
                    const video = item.querySelector('video');
                    if (video) {
                        participantName = video.getAttribute('title') || video.getAttribute('alt') || '';
                    }
                }

                // Method 6: Check all text nodes in the gallery item for potential names
                if (!participantName) {
                    const walker = document.createTreeWalker(
                        item,
                        NodeFilter.SHOW_TEXT,
                        null,
                        false
                    );
                    let node;
                    while (node = walker.nextNode()) {
                        const text = node.textContent?.trim() || '';
                        if (text && text.length > 2 && text.length < 50 &&
                            !text.includes('has joined') && !text.includes('joined') &&
                            !text.match(/^\d+$/) && // Not just numbers
                            !text.match(/^[^\w\s]+$/) // Not just symbols
                        ) {
                            participantName = text;
                            break;
                        }
                    }
                }

                // Add therapist name at bottom left (first participant)
                if (index === 0 && participantName) {
                    let nameLabel = item.querySelector('.therapist-name-label');
                    if (!nameLabel) {
                        nameLabel = document.createElement('div');
                        nameLabel.className = 'therapist-name-label';
                        item.appendChild(nameLabel);
                    }
                    if (nameLabel.textContent !== participantName) {
                        nameLabel.textContent = participantName;
                    }
                }

                // Add patient name at top (second participant)
                if (index === 1 && participantName) {
                    let nameLabel = item.querySelector('.patient-name-label');
                    if (!nameLabel) {
                        nameLabel = document.createElement('div');
                        nameLabel.className = 'patient-name-label';
                        item.appendChild(nameLabel);
                    }
                    if (nameLabel.textContent !== participantName) {
                        nameLabel.textContent = participantName;
                    }
                }
            });
        };

        // Try to add names immediately and set up observer
        const tryAddNames = () => {
            addParticipantNames();
        };

        // Try multiple times as participants might join with delay
        const interval = setInterval(tryAddNames, 1000);
        tryAddNames(); // Try immediately

        // Also observe for DOM changes
        const observer = new MutationObserver(() => {
            addParticipantNames();
        });

        if (sessionContainerRef.current) {
            observer.observe(sessionContainerRef.current, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            clearInterval(interval);
            observer.disconnect();
        };
    }, [sessionJoined, sessionData]);

    // Initialize settings component when session is joined
    useEffect(() => {
        if (!sessionJoined) return;

        const initializeSettings = () => {
            try {
                const settingsContainer = document.getElementById('settingsContainer');
                if (settingsContainer) {
                    uitoolkit.showSettingsComponent(settingsContainer);
                    logger.debug('Settings component initialized');
                }
            } catch (error) {
                logger.error('Error initializing settings component:', error);
            }
        };

        // Try to initialize settings with a delay to ensure DOM is ready
        const timer = setTimeout(initializeSettings, 1000);

        return () => clearTimeout(timer);
    }, [sessionJoined]);

    const toggleNotes = () => {
        setShowNotes((prev) => !prev);
    };

    // Close notes when clicking outside
    useEffect(() => {
        if (!showNotes) return;

        const handleClickOutside = (event) => {
            // Check if click is outside the notes panel and not on the notes button
            if (
                notesPanelRef.current &&
                !notesPanelRef.current.contains(event.target) &&
                !event.target.closest('button[aria-label="Notes"]') &&
                !event.target.closest('.tooltip-button')
            ) {
                setShowNotes(false);
            }
        };

        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNotes]);

    return (
        <>
            {endLoading || isSaving ? (
                // Loader section
                <div className="loader-container absolute inset-0 flex justify-center items-center bg-white bg-opacity-75">
                    <Loader />
                </div>
            ) : (
                // Main content when loading is complete
                <div className="video-screen relative">
                    {/* Note-Taker Section */}
                    {showNotes && (
                        <aside
                            ref={notesPanelRef}
                            className="fixed top-0 right-0 w-[30vw] h-full bg-gray-100 p-4 overflow-y-auto z-40 shadow-lg"
                        >
                            <SessionNote sessionId={sessionData?.data?.id} />
                        </aside>
                    )}

                    {/* Settings Container - Hidden but available for UI Toolkit */}
                    <div ref={settingsContainerRef} id="settingsContainer" className="hidden"></div>



                    {/* Main Video Session Section */}
                    <main className="relative">
                        {/* Top Control Bar */}
                        <header className="header flex justify-between items-center h-16 text-white px-4 md:px-8 bg-[#2D2D2D]">
                            {/* Timer with clock icon */}
                            <div className="flex items-center gap-2">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth={2} />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
                                </svg>
                                <span className="text-white text-sm font-medium">{currentTime}</span>
                            </div>

                            {/* Center Control Buttons */}
                            <div className="flex items-center gap-2">
                                {/* Notes - Show when session is joined */}
                                {sessionJoined && (
                                    <button
                                        onClick={toggleNotes}
                                        className={`relative flex items-center justify-center p-2 hover:bg-gray-700 rounded-lg transition-colors tooltip-button ${showNotes ? 'bg-gray-600' : ''}`}
                                        aria-label="Notes"
                                        title="Notes"
                                    >
                                        <div className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </div>
                                        <span className="tooltip-text">Notes</span>
                                    </button>
                                )}

                                {/* Divider - Show when session is joined and Zoom controls are rendered */}
                                {sessionJoined && zoomControlsRendered && (
                                    <div className="w-px h-12 bg-gray-600 mx-2"></div>
                                )}

                                {/* Zoom Controls Container */}
                                <div className="flex items-center gap-2" id="zoom-controls-header">
                                    {/* Zoom controls will be moved here via CSS */}
                                </div>
                            </div>

                            {/* Empty space for balance (timer is on left) */}
                            <div className="w-16"></div>
                        </header>

                        <section className="session h-full">
                            {/* Full width Video Container */}
                            <div className="w-full h-full relative">
                                <div ref={sessionContainerRef} id="sessionContainer" className="h-full w-full"></div>
                            </div>
                        </section>
                    </main>
                </div>
            )}
        </>
    );
}

export default VideoScreen;